import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { api, ANLAGEN_TYPEN, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Sun, Flame, Upload, Image as ImageIcon, Trash2, Camera, FileText as FileTextIcon2, FileSignature, Wrench, Building2, Users, Sparkles, Paperclip, Package, Search, MessageSquare, ArrowUp, ArrowDown, StickyNote, Phone, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { ProtokollDialog } from "@/components/ProtokollDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProjectNotes } from "@/components/ProjectNotes";
import { ExistingCustomerHint } from "@/components/kunden/ExistingCustomerHint";
import { NotesCell } from "@/components/NotesCell";
import { ProjectAnmeldungen } from "@/components/ProjectAnmeldungen";
import { BulkAuftraggeberBar } from "@/components/BulkAuftraggeberBar";
import { findMonteurConflicts, conflictText, warnMonteurConflicts } from "@/pages/Projekttermine";

// Textarea mit lokalem State: Server-Save erst nach Tipp-Pause (800 ms) oder beim Verlassen.
// Verhindert Buchstaben-Verlust durch Server-Round-Trip & setSelected()-Rerender pro Keystroke.
function PhaseNotesEditor({ value, onSave, testid, placeholder = "Notizen zur Phase …" }) {
  const [local, setLocal] = useState(value || "");
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const latestRef = useRef(local);
  latestRef.current = local;

  // Nur sync vom Server, wenn der User gerade nicht aktiv editiert
  useEffect(() => {
    if (!dirtyRef.current) setLocal(value || "");
  }, [value]);

  const scheduleSave = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (next !== (value || "")) {
        dirtyRef.current = false;
        onSave(next);
      }
    }, 800);
  };

  const flushSave = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (dirtyRef.current && latestRef.current !== (value || "")) {
      dirtyRef.current = false;
      onSave(latestRef.current);
    } else {
      dirtyRef.current = false;
    }
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <Textarea
      rows={2}
      placeholder={placeholder}
      value={local}
      onChange={(e) => { dirtyRef.current = true; setLocal(e.target.value); scheduleSave(e.target.value); }}
      onBlur={flushSave}
      data-testid={testid}
    />
  );
}

const PROJECT_TYPES = { pv: "Photovoltaik", waermepumpe: "Wärmepumpe" };
const MATERIAL_STATUS = {
  offen: { label: "Offen", cls: "bg-slate-100 text-slate-900 dark:bg-slate-800/60 dark:text-slate-200" },
  bestellt: { label: "Bestellt", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300" },
  geliefert: { label: "Geliefert", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300" },
};
const PHASE_STATUS = {
  offen: { label: "Offen", cls: "bg-slate-100 text-slate-900 dark:bg-slate-800/60 dark:text-slate-200", dot: "bg-slate-400" },
  geplant: { label: "Geplant", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300", dot: "bg-amber-500" },
  in_arbeit: { label: "In Arbeit", cls: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300", dot: "bg-sky-500" },
  fertig: { label: "Fertig", cls: "bg-teal-100 text-teal-900 dark:bg-teal-900/30 dark:text-teal-300", dot: "bg-teal-500" },
  abgenommen: { label: "Abgenommen", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300", dot: "bg-emerald-500" },
};

export default function Projekte() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [monteure, setMonteure] = useState([]);
  const [auftraggeber, setAuftraggeber] = useState([]);
  const [deeplinkHandled, setDeeplinkHandled] = useState(false);  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState({ project_type: "pv", pv_variant: "dc_ac", anlagen_typen: [], kunde_name: "", kunde_adresse: "", kunde_plz: "", kunde_ort: "", telefon: "", email: "", auftraggeber_id: "none" });
  const [materialFile, setMaterialFile] = useState(null);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [extractedPreview, setExtractedPreview] = useState([]);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [extractDragOver, setExtractDragOver] = useState(false);
  const [phaseDropKey, setPhaseDropKey] = useState(null);
  const [materialDropOver, setMaterialDropOver] = useState(false);
  const [materialExtracting, setMaterialExtracting] = useState(false);
  const [projectFilesDropOver, setProjectFilesDropOver] = useState(false);
  const [projectFilesUploading, setProjectFilesUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [pvFilter, setPvFilter] = useState("all"); // all | dc_ac | dc | ac
  const [selected, setSelected] = useState(null);
  const [protokoll, setProtokoll] = useState(null); // {projectId, phaseKey, kundeName}
  const [selectedAg, setSelectedAg] = useState("__all__"); // filter
  const [selectedStatus, setSelectedStatus] = useState("all"); // all | neu | planung | fertig
  const [selectedType, setSelectedType] = useState("all"); // all | pv | waermepumpe
  const [addMatOpen, setAddMatOpen] = useState(false);
  const [newMat, setNewMat] = useState({ artikel: "", menge: 1, einheit: "Stk", lieferant: "", status: "offen" });
  const [activePhase, setActivePhase] = useState(null); // for WP phase selector
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState(null); // "kunde" | "status" | "termin" | "material"
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const [unreadByProject, setUnreadByProject] = useState({});
  const [latestNotes, setLatestNotes] = useState({});
  const [bulkSel, setBulkSel] = useState(() => new Set());
  const toggleBulk = (id) => setBulkSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  useEffect(() => {
    api.get("/projects-notes/latest").then((r) => setLatestNotes(r.data || {})).catch(() => {});
  }, []);
  const saveProjectNotiz = async (p, notizen) => {
    try {
      await api.patch(`/projects/${p.id}`, { notizen });
      setItems((list) => list.map((x) => (x.id === p.id ? { ...x, notizen } : x)));
      toast.success("Notiz gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };
  const [unreadInternalByProject, setUnreadInternalByProject] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const [returnPath, setReturnPath] = useState(null);

  const ALL = "__all__";
  const UNASSIGNED = "__unassigned__";

  const isAdmin = ["admin","innendienst"].includes(user?.role);
  const isAuftraggeber = user?.role === "auftraggeber";

  const agName = (id) => auftraggeber.find((a) => a.id === id)?.name;

  // Project status buckets by phase statuses
  const projectBucket = (p) => {
    const phases = p.phases || [];
    if (phases.length === 0) return "neu";
    const statuses = phases.map((ph) => ph.status);
    if (statuses.every((s) => s === "offen")) return "neu";
    if (statuses.every((s) => s === "fertig" || s === "abgenommen")) return "fertig";
    return "planung";
  };

  const bucketCounts = useMemo(() => {
    const c = { neu: 0, planung: 0, fertig: 0 };
    for (const p of items) c[projectBucket(p)]++;
    return c;
  }, [items]);

  // Netzbetreiber + Anlagentypen-Lookup aus verknüpften Anmeldungen (für Suche)
  const [anmeldungenById, setAnmeldungenById] = useState({});
  useEffect(() => {
    api.get("/anmeldungen").then((r) => {
      const map = {};
      for (const a of r.data || []) map[a.id] = a;
      setAnmeldungenById(map);
    }).catch(() => {});
  }, []);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((it) => {
      if (selectedAg !== ALL) {
        if (selectedAg === UNASSIGNED && it.auftraggeber_id) return false;
        if (selectedAg !== UNASSIGNED && it.auftraggeber_id !== selectedAg) return false;
      }
      if (selectedStatus !== "all" && projectBucket(it) !== selectedStatus) return false;
      if (selectedType !== "all" && it.project_type !== selectedType) return false;
      if (q) {
        const anm = it.anmeldung_id ? anmeldungenById[it.anmeldung_id] : null;
        const typenLabels = (it.anlagen_typen || []).map((t) => (ANLAGEN_TYPEN?.[t] || t));
        const hay = [
          it.kunde_name, it.kunde_adresse, it.kunde_plz, it.kunde_ort, it.telefon, it.email,
          ...typenLabels,
          anm?.netzbetreiber, anm?.zaehlernummer,
          ...(anm?.anlagen_typen || []).map((t) => (ANLAGEN_TYPEN?.[t] || t)),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, selectedAg, selectedStatus, selectedType, searchQuery, anmeldungenById]);

  const typeCounts = useMemo(() => ({
    pv: items.filter((i) => i.project_type === "pv").length,
    waermepumpe: items.filter((i) => i.project_type === "waermepumpe").length,
  }), [items]);

  const unassignedCount = useMemo(() => items.filter((i) => !i.auftraggeber_id).length, [items]);

  const groupByAg = (list) => {
    const groups = new Map();
    for (const p of list) {
      const key = p.auftraggeber_id || "__none__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    // sort each group's items by created_at desc
    for (const arr of groups.values()) {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    // order groups: named auftraggeber alphabetically, unassigned last
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === "__none__") return 1;
      if (b === "__none__") return -1;
      return (agName(a) || "").localeCompare(agName(b) || "");
    });
  };

  const pvItems = filteredItems.filter((p) => p.project_type === "pv" && (
    pvFilter === "all" || (p.pv_variant || "dc_ac") === pvFilter
  ));
  const wpItems = filteredItems.filter((p) => p.project_type === "waermepumpe");
  const pvGroups = groupByAg(pvItems);
  const wpGroups = groupByAg(wpItems);

  const agProjectCount = useMemo(() => {
    const map = {};
    for (const p of items) {
      const k = p.auftraggeber_id || "__none__";
      map[k] = (map[k] || 0) + 1;
    }
    return map;
  }, [items]);

  const load = async () => {
    try {
      const [ps, ms, ags, ur, urInt] = await Promise.all([
        api.get("/projects").then((x) => x.data),
        isAdmin ? api.get("/monteure").then((x) => x.data).catch(() => []) : Promise.resolve([]),
        isAdmin ? api.get("/auftraggeber").then((x) => x.data).catch(() => []) : Promise.resolve([]),
        api.get("/projects/notes/unread-counts").then((x) => x.data).catch(() => ({})),
        isAdmin ? api.get("/projects/notes/unread-counts?internal=true").then((x) => x.data).catch(() => ({})) : Promise.resolve({}),
      ]);
      setItems(ps);
      setMonteure(ms);
      setAuftraggeber(ags);
      setUnreadByProject(ur || {});
      setUnreadInternalByProject(urInt || {});
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  // Deep-link: /projekte?open={projectId} → open detail dialog (once per mount)
  useEffect(() => {
    if (deeplinkHandled) return;
    const openId = searchParams.get("open");
    if (!openId || !items.length) return;
    const proj = items.find((p) => p.id === openId);
    if (proj) {
      setSelected(proj);
      // Falls wir per Deep-Link von einer anderen Seite (z. B. /projekttermine) kamen,
      // Rücksprungpfad merken, damit das Schließen des Dialogs dorthin zurückführt.
      if (location.state?.from) setReturnPath(location.state.from);
      setDeeplinkHandled(true);
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // When a project is selected, set initial active WP phase
  useEffect(() => {
    if (selected?.project_type === "waermepumpe" && selected.phases?.length) {
      const cur = selected.phases.find((p) => p.key === activePhase);
      if (!cur) {
        const withTermin = selected.phases.find((p) => p.termin && p.status !== "abgenommen" && p.status !== "fertig");
        const firstOpen = selected.phases.find((p) => p.status !== "abgenommen" && p.status !== "fertig");
        setActivePhase((withTermin || firstOpen || selected.phases[0]).key);
      }
    } else {
      setActivePhase(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...creating, auftraggeber_id: creating.auftraggeber_id === "none" ? null : creating.auftraggeber_id };
      const { data: newProject } = await api.post("/projects", payload);
      // Materialliste-Upload direkt nach Erstellen, falls Datei ausgewählt
      if (materialFile && newProject?.id) {
        setUploadingMaterial(true);
        try {
          const fd = new FormData();
          fd.append("file", materialFile);
          const { data: mres } = await api.post(`/projects/${newProject.id}/materials/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(`Projekt erstellt + ${mres?.added ?? 0} Materialpositionen erkannt`);
        } catch (mErr) {
          toast.success("Projekt erstellt");
          toast.error(formatApiErrorDetail(mErr.response?.data?.detail) || "Materialliste konnte nicht verarbeitet werden");
        } finally {
          setUploadingMaterial(false);
        }
      } else {
        toast.success("Projekt erstellt");
      }
      setOpen(false);
      setCreating({ project_type: "pv", pv_variant: "dc_ac", anlagen_typen: [], kunde_name: "", kunde_adresse: "", kunde_plz: "", kunde_ort: "", telefon: "", email: "", auftraggeber_id: "none" });
      setMaterialFile(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleAnlagenTyp = (t) => {
    setCreating((c) => {
      const s = new Set(c.anlagen_typen || []);
      if (s.has(t)) s.delete(t); else s.add(t);
      return { ...c, anlagen_typen: Array.from(s) };
    });
  };

  const extractProject = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/documents/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      const fullName = [ex.kunde_vorname, ex.kunde_nachname].filter(Boolean).join(" ").trim();
      setCreating((c) => ({
        ...c,
        kunde_name: fullName || c.kunde_name,
        kunde_adresse: ex.kunde_strasse || c.kunde_adresse,
        kunde_plz: ex.kunde_plz || c.kunde_plz,
        kunde_ort: ex.kunde_ort || c.kunde_ort,
        telefon: ex.telefon || c.telefon,
        email: ex.email || c.email,
      }));
      toast.success("Kundendaten aus Dokument übernommen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraktion fehlgeschlagen");
    } finally {
      setExtracting(false);
    }
  };

  const updatePhase = async (projectId, phaseKey, changes, { silent = false } = {}) => {
    const project = items.find((p) => p.id === projectId);
    if (!project) return;
    const newPhases = project.phases.map((p) => (p.key === phaseKey ? { ...p, ...changes } : p));
    try {
      const { data } = await api.patch(`/projects/${projectId}`, { phases: newPhases });
      setItems((it) => it.map((x) => (x.id === projectId ? data : x)));
      if (selected?.id === projectId) setSelected(data);
      if (!silent) toast.success("Phase aktualisiert");
      if ("monteur_id" in changes || "termin" in changes || "termin_ende" in changes || "uhrzeit_von" in changes || "uhrzeit_bis" in changes) {
        const ph = (data.phases || []).find((x) => x.key === phaseKey);
        const others = items.map((x) => (x.id === projectId ? data : x));
        if (ph) warnMonteurConflicts(findMonteurConflicts(others, ph.monteur_id, ph.termin, ph.termin_ende, projectId, phaseKey, ph.uhrzeit_von, ph.uhrzeit_bis), monteure.find((m) => m.id === ph.monteur_id)?.name);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadPhoto = async (projectId, phaseKey, eOrFile) => {
    // Akzeptiert entweder ein change-Event vom <input type="file"> oder direkt ein File-Objekt (Drag&Drop).
    let file = null;
    if (eOrFile && eOrFile.target && "files" in eOrFile.target) {
      file = eOrFile.target.files?.[0];
      eOrFile.target.value = "";
    } else if (eOrFile instanceof File) {
      file = eOrFile;
    }
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${projectId}/phases/${phaseKey}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Datei hochgeladen");
      const { data } = await api.get(`/projects/${projectId}`);
      setItems((it) => it.map((x) => (x.id === projectId ? data : x)));
      if (selected?.id === projectId) setSelected(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const extractMaterialsFromFile = async (file) => {
    if (!file || !selected) return;
    setMaterialExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Preview-Mode: Erst anzeigen, dann vom User übernehmen lassen
      const { data } = await api.post(`/projects/${selected.id}/materials/extract?commit=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.items?.length) {
        toast.info("Keine Positionen erkannt — bitte PDF prüfen");
        return;
      }
      setExtractedPreview(data.items.map((it) => ({ ...it, _accept: true })));
      setExtractDialogOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraktion fehlgeschlagen");
    } finally {
      setMaterialExtracting(false);
    }
  };

  const commitExtractedMaterials = async () => {
    if (!selected) return;
    const items = extractedPreview.filter((it) => it._accept).map((it) => ({
      artikel: it.artikel, menge: Number(it.menge) || 1, einheit: it.einheit || "Stk",
      lieferant: it.lieferant || "", status: it.status || "offen", notizen: it.notizen || "",
    }));
    if (!items.length) {
      setExtractDialogOpen(false);
      setExtractedPreview([]);
      return;
    }
    try {
      const { data } = await api.post(`/projects/${selected.id}/materials/bulk`, { items });
      toast.success(`${data.added} Positionen übernommen`);
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
      setExtractDialogOpen(false);
      setExtractedPreview([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Übernahme fehlgeschlagen");
    }
  };

  const uploadProjectFiles = async (fileList) => {
    if (!selected) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setProjectFilesUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/projects/${selected.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload fehlgeschlagen: ${f.name}`);
      }
    }
    try {
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
    } catch {}
    setProjectFilesUploading(false);
    if (ok) toast.success(`${ok} Datei${ok === 1 ? "" : "en"} hochgeladen`);
  };

  const deleteProjectFile = async (fid) => {
    if (!selected) return;
    if (!window.confirm("Datei wirklich löschen?")) return;
    try {
      await api.delete(`/projects/${selected.id}/files/${fid}`);
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
      toast.success("Datei gelöscht");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  const deletePhoto = async (projectId, phaseKey, photoId, name) => {
    if (!window.confirm(`Datei „${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/projects/${projectId}/phases/${phaseKey}/photos/${photoId}`);
      toast.success("Datei gelöscht");
      const { data } = await api.get(`/projects/${projectId}`);
      setItems((it) => it.map((x) => (x.id === projectId ? data : x)));
      if (selected?.id === projectId) setSelected(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen"); }
  };

  const del = async (pid) => {
    if (!window.confirm("Projekt wirklich löschen?")) return;
    try {
      await api.delete(`/projects/${pid}`);
      toast.success("Gelöscht");
      setSelected(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updateMaterial = async (index, changes) => {
    if (!selected) return;
    try {
      await api.patch(`/projects/${selected.id}/materials/${index}`, changes);
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update fehlgeschlagen"); }
  };

  const deleteMaterial = async (index) => {
    if (!selected) return;
    if (!window.confirm("Materialposition wirklich löschen?")) return;
    try {
      await api.delete(`/projects/${selected.id}/materials/${index}`);
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
      toast.success("Position gelöscht");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen"); }
  };

  const convertPvVariant = async (target) => {
    if (!selected) return;
    const map = { dc_ac: "DC + AC", dc: "Nur DC", ac: "Nur AC" };
    const msg = target === "dc_ac"
      ? `Dieses Projekt auf „DC + AC" hochstufen? Die fehlende Phase wird ergänzt, bestehende Fotos, Termine und Notizen bleiben erhalten.`
      : `Wirklich auf „${map[target]}" umstellen?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.patch(`/projects/${selected.id}/pv-variant`, { pv_variant: target });
      setItems((it) => it.map((x) => (x.id === selected.id ? data : x)));
      setSelected(data);
      toast.success(`Modus auf „${map[target]}" umgestellt`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Umstellung fehlgeschlagen");
    }
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      await api.post(`/projects/${selected.id}/materials`, { ...newMat, menge: Number(newMat.menge) || 1 });
      const { data: refreshed } = await api.get(`/projects/${selected.id}`);
      setItems((it) => it.map((x) => (x.id === selected.id ? refreshed : x)));
      setSelected(refreshed);
      setAddMatOpen(false);
      setNewMat({ artikel: "", menge: 1, einheit: "Stk", lieferant: "", status: "offen" });
      toast.success("Position hinzugefügt");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Hinzufügen fehlgeschlagen"); }
  };

  const renderProjectCard = (p) => {
    const unread = (unreadByProject?.[p.id] || 0) + (unreadInternalByProject?.[p.id] || 0);
    return (
    <Card key={p.id} className="border-border cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setSelected(p)} data-testid={`projekt-${p.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {p.project_type === "pv" ? <Sun className="h-4 w-4 text-primary" /> : <Flame className="h-4 w-4 text-orange-500" />}
              <Badge variant="outline" className="text-[10px]">{PROJECT_TYPES[p.project_type]}</Badge>
            </div>
            <div className="font-medium mt-2 truncate">{p.kunde_name}</div>
            <div className="text-xs text-muted-foreground truncate">{p.kunde_adresse}</div>
          </div>
          {unread > 0 && (
            <span
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-2 py-1"
              title={`${unread} neue Nachricht${unread === 1 ? "" : "en"}`}
              data-testid={`card-unread-${p.id}`}
            >
              <MessageSquare className="h-3 w-3" /> {unread}
            </span>
          )}
        </div>
        <div className="mt-4 space-y-3">
          {p.project_type === "pv" ? (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                  <Sun className="h-3 w-3" /> DC · Dachmontage
                </div>
                {p.phases.filter((x) => x.key === "dc_montage").map((ph) => (
                  <div key={ph.key} className="flex items-center justify-between text-xs gap-2">
                    <span className="truncate text-muted-foreground">
                      Status
                      {ph.termin && (
                        <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-foreground/80 font-mono" data-testid={`card-termin-dc-${p.id}`}>
                          · {new Date(ph.termin + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                        </span>
                      )}
                    </span>
                    <span className={`px-2 py-0.5 rounded shrink-0 ${PHASE_STATUS[ph.status]?.cls}`}>{PHASE_STATUS[ph.status]?.label}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                  <Wrench className="h-3 w-3" /> AC · Elektroinstallation
                </div>
                {p.phases.filter((x) => x.key === "ac_montage").map((ph) => (
                  <div key={ph.key} className="flex items-center justify-between text-xs gap-2">
                    <span className="truncate text-muted-foreground">
                      Status
                      {ph.termin && (
                        <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-foreground/80 font-mono" data-testid={`card-termin-ac-${p.id}`}>
                          · {new Date(ph.termin + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                        </span>
                      )}
                    </span>
                    <span className={`px-2 py-0.5 rounded shrink-0 ${PHASE_STATUS[ph.status]?.cls}`}>{PHASE_STATUS[ph.status]?.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              {p.phases.map((ph) => (
                <div key={ph.key} className="flex items-center justify-between text-xs gap-2">
                  <span className="truncate">
                    {ph.label}
                    {ph.termin && (
                      <span className="ml-1.5 text-[10px] text-foreground/70 font-mono" data-testid={`card-termin-${ph.key}-${p.id}`}>
                        · {new Date(ph.termin + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                      </span>
                    )}
                  </span>
                  <span className={`px-2 py-0.5 rounded shrink-0 ${PHASE_STATUS[ph.status]?.cls}`}>{PHASE_STATUS[ph.status]?.label}</span>
                </div>
              ))}
            </div>
          )}
          {/* Material-Status-Übersicht */}
          {(() => {
            const mats = p.materials || [];
            if (mats.length === 0) {
              return (
                <div className="border-t border-border pt-3" data-testid={`mat-summary-${p.id}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <Package className="h-3 w-3" /> Material
                  </div>
                  <div className="text-xs text-muted-foreground italic">Keine Materialliste hinterlegt</div>
                </div>
              );
            }
            const counts = { offen: 0, bestellt: 0, geliefert: 0 };
            for (const m of mats) counts[m.status || "offen"] = (counts[m.status || "offen"] || 0) + 1;
            const total = mats.length;
            const allDelivered = counts.geliefert === total;
            const allOrdered = counts.bestellt + counts.geliefert === total && counts.geliefert < total;
            const headline = allDelivered
              ? { label: "Komplett im Lager", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300" }
              : allOrdered
              ? { label: "Vollständig bestellt", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300" }
              : counts.offen === total
              ? { label: "Bestellung offen", cls: "bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-300" }
              : { label: "Teilweise bestellt", cls: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300" };
            return (
              <div className="border-t border-border pt-3" data-testid={`mat-summary-${p.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Package className="h-3 w-3" /> Material ({total})
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${headline.cls}`} data-testid={`mat-headline-${p.id}`}>
                    {headline.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] flex-wrap">
                  {counts.offen > 0 && (
                    <span className={`px-1.5 py-0.5 rounded ${MATERIAL_STATUS.offen.cls}`} data-testid={`mat-count-offen-${p.id}`}>
                      {counts.offen}× muss bestellt
                    </span>
                  )}
                  {counts.bestellt > 0 && (
                    <span className={`px-1.5 py-0.5 rounded ${MATERIAL_STATUS.bestellt.cls}`} data-testid={`mat-count-bestellt-${p.id}`}>
                      {counts.bestellt}× bestellt
                    </span>
                  )}
                  {counts.geliefert > 0 && (
                    <span className={`px-1.5 py-0.5 rounded ${MATERIAL_STATUS.geliefert.cls}`} data-testid={`mat-count-geliefert-${p.id}`}>
                      {counts.geliefert}× im Lager
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </CardContent>
    </Card>
    );
  };

  const renderAgGroups = (groups, emptyLabel) => {
    const flat = groups.flatMap(([, ps]) => ps);
    if (flat.length === 0) {
      return (
        <div className="text-center text-sm text-muted-foreground py-12 border border-dashed border-border rounded-md" data-testid="projekte-empty">
          {emptyLabel}
        </div>
      );
    }
    const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : null;
    const nextTermin = (p) => {
      const today = new Date().toISOString().slice(0, 10);
      return (p.phases || [])
        .filter((ph) => ph.termin && ph.termin >= today)
        .sort((a, b) => a.termin.localeCompare(b.termin))[0];
    };
    const materialSummary = (mats) => {
      if (!mats?.length) return null;
      const counts = { offen: 0, bestellt: 0, geliefert: 0 };
      for (const m of mats) counts[m.status || "offen"] = (counts[m.status || "offen"] || 0) + 1;
      const total = mats.length;
      if (counts.geliefert === total) return { label: "Lager", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300" };
      if (counts.bestellt + counts.geliefert === total) return { label: "Bestellt", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300" };
      if (counts.offen === total) return { label: "Offen", cls: "bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-300" };
      return { label: `${counts.geliefert}/${total}`, cls: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300" };
    };
    // Status-Rank: höhere Zahl = "weiter fortgeschritten"
    const statusRank = { offen: 0, geplant: 1, in_arbeit: 2, fertig: 3, abgenommen: 4 };
    const primaryStatusValue = (p) => {
      const relevant = p.project_type === "pv"
        ? (p.phases || []).find((ph) => ph.key === "dc_montage")
        : (p.phases || []).find((ph) => ph.key === "montagetermin");
      return statusRank[relevant?.status] ?? -1;
    };
    const primaryTerminValue = (p) => {
      const relevant = p.project_type === "pv"
        ? (p.phases || []).find((ph) => ph.key === "dc_montage")
        : (p.phases || []).find((ph) => ph.key === "montagetermin");
      return relevant?.termin || "";
    };
    const materialRank = (p) => {
      const m = materialSummary(p.materials);
      if (!m) return -1;
      return { "Offen": 0, "Bestellt": 1, "Lager": 3 }[m.label] ?? 2;
    };
    const sorted = (() => {
      if (!sortCol) return flat;
      const dir = sortDir === "asc" ? 1 : -1;
      const keyFn = {
        kunde: (p) => (p.kunde_name || "").toLowerCase(),
        auftraggeber: (p) => (agName(p.auftraggeber_id) || "").toLowerCase(),
        typ: (p) => p.project_type || "",
        status: (p) => primaryStatusValue(p),
        termin_phase: (p) => primaryTerminValue(p) || "9999-99-99",
        termin_next: (p) => nextTermin(p)?.termin || "9999-99-99",
        material: (p) => materialRank(p),
      }[sortCol];
      return [...flat].sort((a, b) => {
        const va = keyFn(a), vb = keyFn(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    })();
    const SortIcon = ({ col }) => sortCol !== col ? null : (
      sortDir === "asc" ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
    );
    return (
      <Card className="border-border overflow-hidden" data-testid="projekte-table-card">
        {isAdmin && (
          <BulkAuftraggeberBar selectedIds={sorted.filter((p) => bulkSel.has(p.id)).map((p) => p.id)} auftraggeber={auftraggeber}
            onClear={() => setBulkSel((s) => { const n = new Set(s); sorted.forEach((p) => n.delete(p.id)); return n; })}
            onDone={() => { setBulkSel(new Set()); load(); }} />
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={sorted.length > 0 && sorted.every((p) => bulkSel.has(p.id))}
                      onCheckedChange={(v) => setBulkSel((s) => { const n = new Set(s); sorted.forEach((p) => (v ? n.add(p.id) : n.delete(p.id))); return n; })}
                      aria-label="Alle auswählen" data-testid="bulk-select-all"
                    />
                  </TableHead>
                )}
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("kunde")} data-testid="sort-kunde">
                  Kunde<SortIcon col="kunde" />
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("auftraggeber")} data-testid="sort-auftraggeber">
                  Auftraggeber<SortIcon col="auftraggeber" />
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("typ")} data-testid="sort-typ">
                  Typ<SortIcon col="typ" />
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("status")} data-testid="sort-status">
                  Status<SortIcon col="status" />
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("termin_next")} data-testid="sort-termin-next">
                  Nächster Termin<SortIcon col="termin_next" />
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("material")} data-testid="sort-material">
                  Material<SortIcon col="material" />
                </TableHead>
                <TableHead data-testid="col-notizen">Notizen</TableHead>
                <TableHead className="text-right">—</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((p) => {
                const unread = (unreadByProject?.[p.id] || 0) + (unreadInternalByProject?.[p.id] || 0);
                const nt = nextTermin(p);
                const mat = materialSummary(p.materials);
                const statusPhases = p.project_type === "pv"
                  ? (p.phases || []).filter((ph) => ["dc_montage", "ac_montage"].includes(ph.key))
                  : (p.phases || []).filter((ph) => ph.status !== "offen").slice(-2);
                return (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-secondary/40"
                    onClick={() => setSelected(p)}
                    data-testid={`projekt-row-${p.id}`}
                  >
                    {isAdmin && (
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={bulkSel.has(p.id)} onCheckedChange={() => toggleBulk(p.id)}
                          aria-label={`${p.kunde_name} auswählen`} data-testid={`bulk-select-${p.id}`} />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate flex items-center gap-2">
                            {p.kunde_name}
                            {unread > 0 && (
                              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 h-4" data-testid={`card-unread-${p.id}`}>
                                <MessageSquare className="h-2.5 w-2.5" /> {unread}
                              </span>
                            )}
                          </div>
                          {(() => {
                            const plzOrt = [p.kunde_plz, p.kunde_ort].filter(Boolean).join(" ");
                            const addr = [p.kunde_adresse, plzOrt && !(p.kunde_adresse || "").includes(plzOrt) ? plzOrt : ""].filter(Boolean).join(", ");
                            const tel = p.telefon || p.kunde_telefon;
                            return (
                              <>
                                {addr && <div className="text-[11px] text-muted-foreground truncate" data-testid={`row-address-${p.id}`}>{addr}</div>}
                                {tel && (
                                  <a href={`tel:${tel.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
                                    data-testid={`row-phone-${p.id}`}>
                                    <Phone className="h-3 w-3" /> {tel}
                                  </a>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.auftraggeber_id ? agName(p.auftraggeber_id) || "—" : <span className="italic">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] gap-1">
                        {p.project_type === "pv"
                          ? <><Sun className="h-3 w-3 text-primary" /> PV</>
                          : <><Flame className="h-3 w-3 text-orange-500" /> WP</>}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {p.project_type === "pv" ? (
                          ["dc_montage", "ac_montage"].map((key) => {
                            const ph = (p.phases || []).find((x) => x.key === key);
                            if (!ph) return null;
                            const lbl = key === "dc_montage" ? "DC" : "AC";
                            return (
                              <div key={key} className="flex items-center gap-1.5 text-[10px]">
                                <span className="w-6 font-mono text-muted-foreground shrink-0">{lbl}</span>
                                <span className={`px-1.5 py-0.5 rounded ${PHASE_STATUS[ph.status]?.cls}`}>{PHASE_STATUS[ph.status]?.label}</span>
                                {ph.termin ? (
                                  <span className="font-mono text-foreground/70" data-testid={`row-termin-${key}-${p.id}`}>{fmtDate(ph.termin)}</span>
                                ) : (
                                  <span className="italic text-muted-foreground">—</span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          (() => {
                            const mt = (p.phases || []).find((x) => x.key === "montagetermin");
                            if (!mt) return <span className="text-[11px] text-muted-foreground italic">—</span>;
                            return (
                              <div className="flex items-center gap-1.5 text-[10px]">
                                <span className="w-14 font-mono text-muted-foreground shrink-0">Montage</span>
                                <span className={`px-1.5 py-0.5 rounded ${PHASE_STATUS[mt.status]?.cls}`}>{PHASE_STATUS[mt.status]?.label}</span>
                                {mt.termin ? (
                                  <span className="font-mono text-foreground/70" data-testid={`row-termin-montage-${p.id}`}>{fmtDate(mt.termin)}</span>
                                ) : (
                                  <span className="italic text-muted-foreground">—</span>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {nt ? (
                        <div className="flex flex-col">
                          <span className="font-mono">{fmtDate(nt.termin)}</span>
                          <span className="text-[10px] text-muted-foreground truncate">{nt.label}</span>
                        </div>
                      ) : <span className="text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell>
                      {mat ? (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${mat.cls}`} data-testid={`mat-headline-${p.id}`}>{mat.label}</span>
                      ) : <span className="text-[11px] text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell className="align-top">
                      <NotesCell text={p.notizen} lastNote={latestNotes[p.id]} canEdit={isAdmin}
                        onSave={(v) => saveProjectNotiz(p, v)} testid={`projekt-notizen-${p.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Package className="h-4 w-4 inline-block text-muted-foreground opacity-0 group-hover:opacity-100" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    );
  };

  return (
    <div className="animate-fade-up flex flex-col lg:flex-row" data-testid="projekte-page">
      {isAdmin && (
        <aside className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-border p-4 space-y-1 lg:min-h-[calc(100vh)] overflow-x-auto">
          <div className="overline px-2 pt-2 pb-3 flex items-center gap-2">
            <Wrench className="h-3 w-3" /> Projekttyp
          </div>
          <button onClick={() => setSelectedType("all")}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedType === "all" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid="type-filter-all">
            <span>Alle Typen</span>
            <span className="text-xs opacity-70">{items.length}</span>
          </button>
          <button onClick={() => setSelectedType("pv")}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedType === "pv" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid="type-filter-pv">
            <span className="flex items-center gap-2"><Sun className="h-4 w-4 text-primary" /> Photovoltaik-Projekte</span>
            <span className="text-xs opacity-70">{typeCounts.pv}</span>
          </button>
          <button onClick={() => setSelectedType("waermepumpe")}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedType === "waermepumpe" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid="type-filter-wp">
            <span className="flex items-center gap-2"><Flame className="h-4 w-4 text-purple-500" /> Wärmepumpen-Projekte</span>
            <span className="text-xs opacity-70">{typeCounts.waermepumpe}</span>
          </button>

          <div className="overline px-2 pt-6 pb-3 flex items-center gap-2">
            <Building2 className="h-3 w-3" /> Auftraggeber
          </div>
          <button onClick={() => setSelectedAg(ALL)}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === ALL ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid="ag-filter-all">
            <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Alle</span>
            <span className="text-xs opacity-70">{items.length}</span>
          </button>
          {auftraggeber.map((a) => (
            <button key={a.id} onClick={() => setSelectedAg(a.id)}
              className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === a.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
              data-testid={`ag-filter-${a.id}`}>
              <span className="truncate flex-1 text-left">{a.name}</span>
              <span className="text-xs opacity-70 ml-2">{agProjectCount[a.id] || 0}</span>
            </button>
          ))}
          <button
            onClick={() => setSelectedAg(UNASSIGNED)}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === UNASSIGNED ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid="ag-filter-unassigned">
            <span className="italic opacity-80">Ohne Zuordnung</span>
            <span className="text-xs opacity-70">{unassignedCount}</span>
          </button>
          {auftraggeber.length === 0 && (
            <div className="text-xs text-muted-foreground px-3 pt-3">
              Noch keine Auftraggeber. Lege welche unter „Auftraggeber" an.
            </div>
          )}
        </aside>
      )}

      <div className="flex-1 min-w-0 p-4 lg:p-8 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Installations-Projekte</div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Projekte</h1>
            <p className="text-muted-foreground mt-1 text-sm lg:text-base">
              Photovoltaik (DC + AC) und Wärmepumpen — nach Auftraggeber gruppiert.
            </p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="new-projekt-button" className="w-full sm:w-auto" style={{ display: isAuftraggeber ? "none" : undefined }}>
            <Plus className="h-4 w-4 mr-2" /> Neues Projekt
          </Button>
        </div>

        {/* Suchleiste */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Kunde, Adresse, PLZ, Telefon oder E-Mail suchen …"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="projekte-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground px-2 py-1"
              data-testid="projekte-search-clear"
            >
              ×
            </button>
          )}
        </div>

        {/* Status-Buckets */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="status-tabs">
          {[
            { k: "all", label: "Alle", count: items.length, cls: "bg-slate-100 text-slate-900 dark:bg-slate-800/60 dark:text-slate-200" },
            { k: "neu", label: "Neu", count: bucketCounts.neu, cls: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300", icon: Sparkles },
            { k: "planung", label: "Planung", count: bucketCounts.planung, cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300", icon: Wrench },
            { k: "fertig", label: "Fertiggestellt", count: bucketCounts.fertig, cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300", icon: FileSignature },
          ].map(({ k, label, count, cls, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setSelectedStatus(k)}
              data-testid={`status-tab-${k}`}
              className={`text-left rounded-md border p-3 transition-all ${selectedStatus === k ? "border-primary shadow-sm" : "border-border hover:border-primary/40"}`}
            >
              <div className="flex items-center gap-2">
                {Icon && <Icon className="h-3.5 w-3.5" />}
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>{label}</span>
              </div>
              <div className="font-heading text-2xl font-bold mt-1.5 tracking-tight">{count}</div>
            </button>
          ))}
        </div>

        {/* Segment 1: Photovoltaik */}
        <section data-testid="segment-pv" className="space-y-4 pt-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sun className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-xl lg:text-2xl font-bold tracking-tight">Photovoltaik-Projekte</h2>
                <div className="text-xs text-muted-foreground">{pvItems.length} Projekte</div>
              </div>
            </div>
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="pv-variant-filter">
              {[
                { key: "all",   label: "Alle" },
                { key: "dc_ac", label: "DC + AC" },
                { key: "dc",    label: "Nur DC" },
                { key: "ac",    label: "Nur AC" },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setPvFilter(f.key)}
                  className={`px-3 py-1.5 font-medium transition-colors ${pvFilter === f.key ? "bg-primary text-primary-foreground" : "hover:bg-secondary/60"}`}
                  data-testid={`pv-variant-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {renderAgGroups(pvGroups, "Keine Photovoltaik-Projekte in dieser Ansicht.")}
        </section>

        {/* Segment 2: Wärmepumpe */}
        <section data-testid="segment-wp" className="space-y-4 pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <Flame className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <h2 className="font-heading text-xl lg:text-2xl font-bold tracking-tight">Wärmepumpen-Projekte</h2>
              <div className="text-xs text-muted-foreground">{wpItems.length} Projekte · Auftrag → Bau → Fertig</div>
            </div>
          </div>
          {renderAgGroups(wpGroups, "Keine Wärmepumpen-Projekte in dieser Ansicht.")}
        </section>
      </div>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="new-projekt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Neues Projekt</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Typ</Label>
              <Select value={creating.project_type} onValueChange={(v) => setCreating({ ...creating, project_type: v })}>
                <SelectTrigger data-testid="new-typ"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pv">Photovoltaik</SelectItem>
                  <SelectItem value="waermepumpe">Wärmepumpe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {creating.project_type === "pv" && (
              <div className="col-span-2 space-y-1.5">
                <Label>PV-Umfang</Label>
                <Select value={creating.pv_variant} onValueChange={(v) => setCreating({ ...creating, pv_variant: v })}>
                  <SelectTrigger data-testid="new-pv-variant"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dc_ac">DC + AC (komplette Installation)</SelectItem>
                    <SelectItem value="dc">Nur DC (Dach-Montage)</SelectItem>
                    <SelectItem value="ac">Nur AC (Elektro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Anlagentypen (Mehrfachauswahl) */}
            <div className="col-span-2 space-y-2">
              <Label>Anlagentypen (Mehrfachauswahl)</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-anlagentypen">
                {[
                  { k: "erzeugungsanlage", l: "Erzeugungsanlage (PV)" },
                  { k: "waermepumpe", l: "Wärmepumpe" },
                  { k: "stromspeicher", l: "Stromspeicher" },
                  { k: "wallbox", l: "Wallbox" },
                ].map((opt) => {
                  const checked = (creating.anlagen_typen || []).includes(opt.k);
                  return (
                    <label
                      key={opt.k}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm cursor-pointer transition-colors ${checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                      data-testid={`new-anlagentyp-${opt.k}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAnlagenTyp(opt.k)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/40"
                      />
                      <span>{opt.l}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            {/* Materialliste direkt hochladen (optional) */}
            <div className="col-span-2 space-y-1.5">
              <Label>Materialliste (optional)</Label>
              <label className={`flex items-center justify-between gap-2 w-full rounded-md border border-dashed border-border bg-muted/30 px-3 py-2.5 text-sm cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition-colors ${uploadingMaterial ? "opacity-60 pointer-events-none" : ""}`} data-testid="new-material-file-label">
                <span className="flex items-center gap-2 min-w-0">
                  <Paperclip className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">
                    {materialFile ? materialFile.name : (uploadingMaterial ? "KI extrahiert Positionen …" : "PDF/Bild hochladen — Positionen per KI erkennen")}
                  </span>
                </span>
                {materialFile && !uploadingMaterial && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMaterialFile(null); }}
                    className="text-xs text-muted-foreground hover:text-destructive px-2 shrink-0"
                    data-testid="new-material-file-clear"
                  >
                    Entfernen
                  </button>
                )}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setMaterialFile(f); }}
                  data-testid="new-material-file-input"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">Nach dem Anlegen des Projekts wird die Datei automatisch analysiert und die Positionen als Materialliste übernommen.</p>
            </div>
            <div className="col-span-2">
              <label
                onDragOver={(e) => { if (!extracting) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setExtractDragOver(true); } }}
                onDragLeave={() => setExtractDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setExtractDragOver(false);
                  if (extracting) return;
                  const f = e.dataTransfer.files?.[0];
                  if (f) extractProject(f);
                }}
                className={`flex flex-col items-center justify-center gap-2 w-full rounded-md border-2 border-dashed px-3 py-6 text-sm cursor-pointer transition-colors ${extracting ? "opacity-60 pointer-events-none border-primary/60 bg-primary/5" : extractDragOver ? "border-primary bg-primary/10 ring-2 ring-primary/40" : "border-primary/60 bg-primary/5 hover:bg-primary/10"}`}
                data-testid="new-ai-extract-label"
              >
                <FileTextIcon2 className="h-6 w-6 text-primary" />
                <div className="text-center">
                  <div className="font-medium">
                    {extracting ? "KI analysiert Dokument …" : extractDragOver ? "Datei loslassen zum Übernehmen" : "🪄 Angebot/Auftrag hier hineinziehen"}
                  </div>
                  {!extracting && !extractDragOver && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">oder klicken, um PDF/Bild auszuwählen — Kundendaten werden per KI übernommen</div>
                  )}
                </div>
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) extractProject(f); }} data-testid="new-ai-extract-input" />
              </label>
            </div>
            {isAdmin && (
              <div className="col-span-2 space-y-1.5">
                <Label>Auftraggeber</Label>
                <Select value={creating.auftraggeber_id} onValueChange={(v) => setCreating({ ...creating, auftraggeber_id: v })}>
                  <SelectTrigger data-testid="new-ag"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Kein Auftraggeber —</SelectItem>
                    {auftraggeber.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>Kunde</Label>
              <Input value={creating.kunde_name} onChange={(e) => setCreating({ ...creating, kunde_name: e.target.value })} required data-testid="new-kunde" />
            </div>
            {open && (
              <ExistingCustomerHint data={creating} onUse={(h) => setCreating((c) => ({
                ...c, kunde_name: h.kunde_name, kunde_adresse: h.strasse || c.kunde_adresse, kunde_plz: h.kunde_plz,
                kunde_ort: h.kunde_ort, telefon: h.telefon || c.telefon, email: h.email || c.email,
              }))} />
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>Straße &amp; Hausnr.</Label>
              <Input value={creating.kunde_adresse} onChange={(e) => setCreating({ ...creating, kunde_adresse: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>PLZ</Label>
              <Input value={creating.kunde_plz} onChange={(e) => setCreating({ ...creating, kunde_plz: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Ort</Label>
              <Input value={creating.kunde_ort} onChange={(e) => setCreating({ ...creating, kunde_ort: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefon</Label>
              <Input value={creating.telefon} onChange={(e) => setCreating({ ...creating, telefon: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>E-Mail</Label>
              <Input type="email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="new-projekt-submit">Erstellen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); if (returnPath) { const p = returnPath; setReturnPath(null); navigate(p); } } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="projekt-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight text-2xl flex items-center gap-2">
                  {selected.project_type === "pv" ? <Sun className="h-5 w-5 text-primary" /> : <Flame className="h-5 w-5 text-orange-500" />}
                  {selected.kunde_name}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Projekt-Details: Phasen, Termine, Material und Nachrichten für {selected.kunde_name}
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">{selected.kunde_adresse} · {selected.kunde_plz} {selected.kunde_ort}</div>
              {(() => {
                const tel = selected.telefon || selected.kunde_telefon;
                const mail = selected.email || selected.kunde_email;
                return (
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="project-contact">
                    {tel ? (
                      <a href={`tel:${tel.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="project-telefon">
                        <Phone className="h-3.5 w-3.5" /> {tel}
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground italic" data-testid="project-telefon-missing">
                        <Phone className="h-3.5 w-3.5" /> Keine Telefonnummer hinterlegt
                      </span>
                    )}
                    {mail && (
                      <a href={`mailto:${mail}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary hover:underline" data-testid="project-email">
                        <Mail className="h-3.5 w-3.5" /> {mail}
                      </a>
                    )}
                  </div>
                );
              })()}
              {isAdmin && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="project-auftraggeber-section">
                  <span className="text-xs text-muted-foreground">Auftraggeber:</span>
                  <Select
                    value={selected.auftraggeber_id || "none"}
                    onValueChange={async (v) => {
                      try {
                        const { data } = await api.patch(`/projects/${selected.id}`, { auftraggeber_id: v === "none" ? "" : v });
                        setSelected(data);
                        setItems((list) => list.map((x) => (x.id === data.id ? data : x)));
                        toast.success(v === "none" ? "Auftraggeber entfernt" : `Auftraggeber: ${agName(v) || ""}`);
                      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
                    }}
                  >
                    <SelectTrigger className="h-8 w-64 text-xs" data-testid="project-auftraggeber-select">
                      <SelectValue>{selected.auftraggeber_id ? agName(selected.auftraggeber_id) || "Unbekannter Auftraggeber" : "— ohne Auftraggeber —"}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— ohne Auftraggeber —</SelectItem>
                      {auftraggeber.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`project-auftraggeber-option-${a.id}`}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <ProjectAnmeldungen project={selected} canCreate={isAdmin} />
              {selected.project_type === "pv" && isAdmin && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="pv-variant-section">
                  <span className="text-xs text-muted-foreground">Modus:</span>
                  <span className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-medium" data-testid="pv-variant-current">
                    {selected.pv_variant === "dc" ? "Nur DC" : selected.pv_variant === "ac" ? "Nur AC" : "DC + AC"}
                  </span>
                  {(selected.pv_variant === "dc" || selected.pv_variant === "ac") && (
                    <button
                      type="button"
                      onClick={() => convertPvVariant("dc_ac")}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30 px-2.5 py-1 text-xs font-medium transition-colors"
                      data-testid="pv-variant-upgrade-btn"
                      title={selected.pv_variant === "dc" ? "AC-Phase ergänzen" : "DC-Phase ergänzen"}
                    >
                      <Plus className="h-3.5 w-3.5" /> Auf DC + AC hochstufen
                    </button>
                  )}
                </div>
              )}

              {/* Projekt-Dateiablage (Angebot, Auftragsbestätigung, allgemeine Kundendokumente) */}
              <div
                className={`mt-4 rounded-md border p-4 transition-colors ${projectFilesDropOver ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-border"}`}
                data-testid="project-files-section"
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("Files") && !projectFilesUploading) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    if (!projectFilesDropOver) setProjectFilesDropOver(true);
                  }
                }}
                onDragLeave={() => setProjectFilesDropOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setProjectFilesDropOver(false);
                  if (!projectFilesUploading) uploadProjectFiles(e.dataTransfer.files);
                }}
              >
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-primary" /> Dateiablage ({selected.files?.length || 0})
                    {projectFilesUploading && <span className="text-[10px] text-muted-foreground animate-pulse">wird hochgeladen …</span>}
                  </div>
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline" data-testid="project-files-upload-label">
                    <input
                      type="file"
                      multiple
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="project-files-upload-input"
                      onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; uploadProjectFiles(fs); }}
                    />
                    <Upload className="h-4 w-4" /> Datei hinzufügen
                  </label>
                </div>
                {(selected.files?.length ?? 0) === 0 ? (
                  <div className={`flex flex-col items-center justify-center gap-1 py-6 border-2 border-dashed rounded-md text-xs transition-colors ${projectFilesDropOver ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`} data-testid="project-files-empty">
                    <Upload className="h-5 w-5" />
                    <div>{projectFilesDropOver ? "📥 Dateien loslassen zum Hochladen" : "PDF/Foto hierhin ziehen — z. B. Angebot, Auftragsbestätigung, sonstige Dokumente"}</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {selected.files.map((f) => {
                      const isPdf = f.path?.endsWith(".pdf");
                      return (
                        <div key={f.id} className="relative group" data-testid={`project-file-wrap-${f.id}`}>
                          <button
                            type="button"
                            onClick={() => setPreviewPhoto(f)}
                            className="block w-full rounded border border-border overflow-hidden hover:border-primary transition-colors text-left"
                            data-testid={`project-file-${f.id}`}
                            title={f.name}
                          >
                            {isPdf ? (
                              <div className="aspect-square flex flex-col items-center justify-center bg-muted text-muted-foreground p-2 gap-1">
                                <FileTextIcon2 className="h-8 w-8 text-red-500" />
                                <div className="text-[10px] font-medium truncate w-full text-center px-1" title={f.name}>{f.name}</div>
                              </div>
                            ) : (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${f.path}`} alt={f.name} className="aspect-square object-cover w-full" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteProjectFile(f.id)}
                            className="absolute top-1 right-1 bg-background/95 border border-border rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-opacity"
                            data-testid={`project-file-delete-${f.id}`}
                            title="Datei löschen"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Projekt-Notizen direkt unter der Dateiablage */}
              <div className="mt-4 rounded-md border border-border p-4" data-testid="project-notizen-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium flex items-center gap-2"><StickyNote className="h-4 w-4 text-primary" /> Notizen</div>
                  <span className="text-[10px] text-muted-foreground">Autosave</span>
                </div>
                <PhaseNotesEditor
                  value={selected.notizen || ""}
                  onSave={async (v) => {
                    try {
                      await api.patch(`/projects/${selected.id}`, { notizen: v });
                      setItems((list) => list.map((x) => (x.id === selected.id ? { ...x, notizen: v } : x)));
                      setSelected((s) => (s?.id === selected.id ? { ...s, notizen: v } : s));
                    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
                  }}
                  testid="project-notizen"
                  placeholder="Notizen zum Projekt …"
                />
              </div>

              {/* Materialliste */}
              <div
                className={`mt-4 rounded-md border p-4 transition-colors ${materialDropOver ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-border"}`}
                data-testid="material-section"
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("Files")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    if (!materialDropOver) setMaterialDropOver(true);
                  }
                }}
                onDragLeave={() => setMaterialDropOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setMaterialDropOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) extractMaterialsFromFile(f);
                }}
              >
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <FileTextIcon2 className="h-4 w-4 text-primary" /> Materialliste ({selected.materials?.length || 0})
                    {selected.materials?.some((m) => m.verbaut) && (
                      <span className="text-[10px] font-normal text-primary" data-testid="mat-verbaut-count">
                        · {selected.materials.filter((m) => m.verbaut).length} verbaut
                      </span>
                    )}
                    {materialExtracting && <span className="text-[10px] text-muted-foreground animate-pulse">KI extrahiert …</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setAddMatOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="material-add-manual">
                      <Plus className="h-4 w-4" /> Position hinzufügen
                    </button>
                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline">
                      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" data-testid="material-upload"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) extractMaterialsFromFile(f); }} />
                      <Upload className="h-4 w-4" /> Aus PDF extrahieren
                    </label>
                  </div>
                </div>
                {(selected.materials?.length ?? 0) === 0 && (
                  <div className={`text-xs text-center py-4 border border-dashed rounded-md ${materialDropOver ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid="material-empty-dropzone">
                    {materialDropOver ? "📥 Datei loslassen — Positionen werden per KI erkannt" : 'Noch keine Positionen — PDF/Bild hierhin ziehen oder oben „Aus PDF extrahieren" klicken'}
                  </div>
                )}
                {selected.materials?.length > 0 ? (
                  <ul className="divide-y divide-border/50">
                    {selected.materials.map((m, i) => (
                      <li key={i} className="text-xs flex items-center gap-2 py-2" data-testid={`mat-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="truncate">
                            <span className="font-medium">{m.menge} {m.einheit || "Stk"}</span> · {m.artikel}
                          </div>
                          {m.lieferant && <div className="text-[10px] text-muted-foreground truncate">{m.lieferant}</div>}
                          {m.verbaut && (
                            <div className="text-[10px] text-primary font-medium flex items-center gap-1" data-testid={`mat-verbaut-${i}`}>
                              <CheckCircle2 className="h-3 w-3" /> Verbaut{m.verbaut_von ? ` von ${m.verbaut_von}` : ""}{m.verbaut_at ? ` · ${new Date(m.verbaut_at).toLocaleDateString("de-DE")}` : ""}
                            </div>
                          )}
                        </div>
                        <Select value={m.status || "offen"} onValueChange={(v) => updateMaterial(i, { status: v })}>
                          <SelectTrigger className={`h-7 w-[110px] text-[11px] ${MATERIAL_STATUS[m.status || "offen"]?.cls}`} data-testid={`mat-status-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(MATERIAL_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => deleteMaterial(i)}
                          className="shrink-0 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`mat-delete-${i}`}
                          title="Position löschen"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground">Noch keine Materialpositionen. Lade eine Auftragsbestätigung hoch — Gemini extrahiert automatisch.</div>
                )}
              </div>

              <div className="space-y-4 mt-4">
                {selected.project_type === "waermepumpe" && selected.phases?.length > 0 && (
                  <Card className="border-border bg-muted/30">
                    <CardContent className="p-4">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Aktuelle Phase</Label>
                      <Select value={activePhase || selected.phases[0].key} onValueChange={setActivePhase}>
                        <SelectTrigger className="mt-1.5 h-10" data-testid="wp-phase-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {selected.phases.map((ph, i) => (
                            <SelectItem key={ph.key} value={ph.key}>
                              <span className="inline-flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${PHASE_STATUS[ph.status]?.dot || "bg-slate-400"}`} />
                                <span className="text-[10px] font-mono opacity-60">{i + 1}.</span>
                                <span>{ph.label}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${PHASE_STATUS[ph.status]?.cls}`}>{PHASE_STATUS[ph.status]?.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                )}
                {selected.phases
                  .filter((ph) => selected.project_type !== "waermepumpe" || ph.key === (activePhase || selected.phases[0].key))
                  .map((ph) => (
                  <Card
                    key={ph.key}
                    className={`border-border transition-colors ${phaseDropKey === ph.key ? "border-primary ring-2 ring-primary/40 bg-primary/5" : ""}`}
                    data-testid={`phase-${ph.key}`}
                    onDragOver={(e) => {
                      if (e.dataTransfer.types.includes("Files")) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                        if (phaseDropKey !== ph.key) setPhaseDropKey(ph.key);
                      }
                    }}
                    onDragLeave={() => setPhaseDropKey((k) => (k === ph.key ? null : k))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setPhaseDropKey(null);
                      const files = Array.from(e.dataTransfer.files || []);
                      files.forEach((f) => uploadPhoto(selected.id, ph.key, f));
                    }}
                  >
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{ph.label}</div>
                        <Select value={ph.status} onValueChange={(v) => updatePhase(selected.id, ph.key, { status: v })}>
                          <SelectTrigger className={`w-[150px] h-8 text-xs ${PHASE_STATUS[ph.status]?.cls}`} data-testid={`phase-status-${ph.key}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(PHASE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Termin</Label>
                          <Input type="date" value={ph.termin || ""} onChange={(e) => {
                            const v = e.target.value || null;
                            updatePhase(selected.id, ph.key, { termin: v, ...(!v || (ph.termin_ende && ph.termin_ende <= v) ? { termin_ende: null } : {}) });
                          }} data-testid={`phase-termin-${ph.key}`} />
                          <Input type="date" value={ph.termin_ende || ""} disabled={!ph.termin} title="bis (optional, mehrtägig)"
                            onChange={(e) => {
                              const v = e.target.value || null;
                              if (v && v < ph.termin) { toast.error("„bis“ darf nicht vor dem Termin liegen"); return; }
                              updatePhase(selected.id, ph.key, { termin_ende: v && v > ph.termin ? v : null });
                            }} className="h-8 text-xs" aria-label="bis" data-testid={`phase-termin-ende-${ph.key}`} />
                          <div className="text-[10px] text-muted-foreground">2. Feld: bis (optional, mehrtägig)</div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <Input type="time" value={ph.uhrzeit_von || ""} disabled={!ph.termin} aria-label="Uhrzeit von" title="Uhrzeit von"
                              onChange={(e) => {
                                const v = e.target.value || null;
                                updatePhase(selected.id, ph.key, { uhrzeit_von: v, ...(!v || (ph.uhrzeit_bis && ph.uhrzeit_bis <= v) ? { uhrzeit_bis: null } : {}) }, { silent: true });
                              }} className="h-8 text-xs" data-testid={`phase-zeit-von-${ph.key}`} />
                            <Input type="time" value={ph.uhrzeit_bis || ""} disabled={!ph.uhrzeit_von} aria-label="Uhrzeit bis" title="Uhrzeit bis"
                              onChange={(e) => {
                                const v = e.target.value || null;
                                if (v && v <= ph.uhrzeit_von) { toast.error("Endzeit muss nach der Startzeit liegen"); return; }
                                updatePhase(selected.id, ph.key, { uhrzeit_bis: v }, { silent: true });
                              }} className="h-8 text-xs" data-testid={`phase-zeit-bis-${ph.key}`} />
                          </div>
                          <div className="text-[10px] text-muted-foreground">Uhrzeit von – bis (optional)</div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Monteur</Label>
                          <Select value={ph.monteur_id || "none"} onValueChange={(v) => updatePhase(selected.id, ph.key, { monteur_id: v === "none" ? null : v })}>
                            <SelectTrigger data-testid={`phase-monteur-${ph.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Kein Monteur —</SelectItem>
                              {monteure.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {(() => {
                            const cs = findMonteurConflicts(items, ph.monteur_id, ph.termin, ph.termin_ende, selected.id, ph.key, ph.uhrzeit_von, ph.uhrzeit_bis);
                            return cs.length > 0 && (
                              <div className="text-[11px] rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-amber-800 dark:text-amber-300 px-2 py-1" data-testid={`phase-monteur-conflict-${ph.key}`}>
                                Bereits eingeplant: {cs.map(conflictText).join("; ")}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <PhaseNotesEditor
                        value={ph.notizen || ""}
                        onSave={(v) => updatePhase(selected.id, ph.key, { notizen: v }, { silent: true })}
                        testid={`phase-notes-${ph.key}`}
                      />

                      <div className="border-t border-border pt-3">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                            <Paperclip className="h-3 w-3" /> Fotos &amp; Dateien ({ph.photos?.length || 0})
                            {phaseDropKey === ph.key && (
                              <span className="ml-2 text-[10px] text-primary font-semibold normal-case tracking-normal">📥 loslassen zum Hochladen</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {(ph.key === "abnahme" || ph.key === "dc_montage" || ph.key === "ac_montage" || ph.key === "montagetermin") && (
                              <button type="button" onClick={() => setProtokoll({ projectId: selected.id, phaseKey: ph.key, kundeName: selected.kunde_name })}
                                className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline" data-testid={`protokoll-btn-${ph.key}`}>
                                <FileSignature className="h-4 w-4" /> Protokoll
                              </button>
                            )}
                            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline">
                              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadPhoto(selected.id, ph.key, e)} data-testid={`phase-file-upload-${ph.key}`} />
                              <Upload className="h-4 w-4" /> Datei
                            </label>
                            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline">
                              <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => uploadPhoto(selected.id, ph.key, e)} data-testid={`phase-upload-${ph.key}`} />
                              <Camera className="h-4 w-4" /> Foto
                            </label>
                          </div>
                        </div>
                        {ph.photos?.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {ph.photos.map((ph2) => {
                              const isPdf = ph2.path.endsWith(".pdf");
                              return (
                                <div key={ph2.id} className="relative group" data-testid={`photo-wrap-${ph2.id}`}>
                                  <button
                                    type="button"
                                    onClick={() => setPreviewPhoto(ph2)}
                                    className="block w-full rounded border border-border overflow-hidden hover:border-primary transition-colors text-left"
                                    data-testid={`photo-${ph2.id}`}
                                    title={ph2.name}
                                  >
                                    {isPdf ? (
                                      <div className="aspect-square flex flex-col items-center justify-center bg-muted text-muted-foreground p-2 gap-1">
                                        <FileTextIcon2 className="h-8 w-8 text-red-500" />
                                        <div className="text-[10px] font-medium truncate w-full text-center px-1" title={ph2.name}>{ph2.name}</div>
                                      </div>
                                    ) : (
                                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${ph2.path}`} alt={ph2.name} className="aspect-square object-cover w-full" />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); deletePhoto(selected.id, ph.key, ph2.id, ph2.name); }}
                                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border text-destructive opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-all flex items-center justify-center shadow-sm"
                                    data-testid={`photo-delete-${ph2.id}`}
                                    title="Datei löschen"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className={`flex flex-col items-center justify-center gap-1 py-6 border-2 border-dashed rounded-md text-xs transition-colors ${phaseDropKey === ph.key ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`} data-testid={`phase-dropzone-${ph.key}`}>
                            <Upload className="h-5 w-5" />
                            <div className="text-center">
                              {phaseDropKey === ph.key
                                ? "📥 Datei loslassen zum Hochladen"
                                : 'Foto/PDF hierhin ziehen — oder rechts oben „Datei / Foto" klicken'}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Projekt-Nachrichten */}
              <div className="mt-4" data-testid="projekt-notes-wrapper">
                <ProjectNotes projectId={selected.id} />
                {isAdmin && (
                  <ProjectNotes projectId={selected.id} internal={true} />
                )}
              </div>

              <DialogFooter>
                {isAdmin && (
                  <Button variant="ghost" onClick={() => del(selected.id)} data-testid="projekt-delete">
                    <Trash2 className="h-4 w-4 mr-2 text-destructive" /> Projekt löschen
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelected(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ProtokollDialog
        open={!!protokoll}
        onOpenChange={(v) => !v && setProtokoll(null)}
        projectId={protokoll?.projectId}
        phaseKey={protokoll?.phaseKey}
        kundeName={protokoll?.kundeName}
        onDone={async () => {
          if (protokoll?.projectId) {
            const { data } = await api.get(`/projects/${protokoll.projectId}`);
            setItems((it) => it.map((x) => (x.id === protokoll.projectId ? data : x)));
            if (selected?.id === protokoll.projectId) setSelected(data);
          }
        }}
      />

      {/* Manuelle Material-Position hinzufügen */}
      <Dialog open={addMatOpen} onOpenChange={setAddMatOpen}>
        <DialogContent data-testid="add-material-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> Position hinzufügen
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addMaterial} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Artikel / Bezeichnung *</Label>
              <Input value={newMat.artikel} onChange={(e) => setNewMat({ ...newMat, artikel: e.target.value })} required data-testid="mat-artikel" placeholder="z.B. Meyer Burger 400W Modul" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Menge</Label>
                <Input type="number" step="0.01" min="0" value={newMat.menge} onChange={(e) => setNewMat({ ...newMat, menge: e.target.value })} required data-testid="mat-menge" />
              </div>
              <div className="space-y-1.5">
                <Label>Einheit</Label>
                <Input value={newMat.einheit} onChange={(e) => setNewMat({ ...newMat, einheit: e.target.value })} data-testid="mat-einheit" placeholder="Stk" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={newMat.status} onValueChange={(v) => setNewMat({ ...newMat, status: v })}>
                  <SelectTrigger data-testid="mat-status-new"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MATERIAL_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Lieferant</Label>
              <Input value={newMat.lieferant} onChange={(e) => setNewMat({ ...newMat, lieferant: e.target.value })} data-testid="mat-lieferant" placeholder="optional" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddMatOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="mat-submit">Hinzufügen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Foto/Dokument-Vorschau (Lightbox) */}
      <Dialog open={!!previewPhoto} onOpenChange={(v) => !v && setPreviewPhoto(null)}>
        <DialogContent className="max-w-5xl" data-testid="photo-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-lg flex items-center gap-2 pr-6 min-w-0">
              {previewPhoto?.path?.endsWith(".pdf") ? <FileTextIcon2 className="h-5 w-5 text-red-500 shrink-0" /> : <ImageIcon className="h-5 w-5 text-primary shrink-0" />}
              <span className="truncate">{previewPhoto?.name}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Vorschau der Datei {previewPhoto?.name}</DialogDescription>
          </DialogHeader>
          {previewPhoto && (
            <div className="flex flex-col gap-3">
              <div className="bg-muted/30 rounded-md overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh", maxHeight: "75vh" }}>
                {previewPhoto.path.endsWith(".pdf") ? (
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewPhoto.path}`}
                    className="w-full h-[75vh] border-0"
                    title={previewPhoto.name}
                    data-testid="photo-preview-iframe"
                  />
                ) : (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewPhoto.path}`}
                    alt={previewPhoto.name}
                    className="max-w-full max-h-[75vh] object-contain"
                    data-testid="photo-preview-img"
                  />
                )}
              </div>
              <DialogFooter>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewPhoto.path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="photo-preview-open-tab"
                >
                  <Upload className="h-3.5 w-3.5 rotate-180" /> In neuem Tab öffnen
                </a>
                <Button type="button" variant="outline" onClick={() => setPreviewPhoto(null)} data-testid="photo-preview-close">Schließen</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Material-Review-Dialog */}
      <Dialog open={extractDialogOpen} onOpenChange={(v) => { if (!v) { setExtractDialogOpen(false); setExtractedPreview([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="material-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <FileTextIcon2 className="h-5 w-5 text-primary" /> KI-Vorschlag · {extractedPreview.length} Position{extractedPreview.length === 1 ? "" : "en"}
            </DialogTitle>
            <DialogDescription>
              Prüfe die extrahierten Positionen und übernimm nur das Nötige. Du kannst Menge, Einheit und Bezeichnung direkt anpassen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <button
                type="button"
                className="hover:underline"
                onClick={() => setExtractedPreview((it) => it.map((x) => ({ ...x, _accept: !it.every((y) => y._accept) })))}
                data-testid="material-review-toggle-all"
              >
                {extractedPreview.every((x) => x._accept) ? "Keine auswählen" : "Alle auswählen"}
              </button>
              <span data-testid="material-review-count">
                {extractedPreview.filter((x) => x._accept).length} / {extractedPreview.length} ausgewählt
              </span>
            </div>
            <div className="rounded-md border border-border divide-y divide-border/60">
              {extractedPreview.map((it, idx) => (
                <div key={idx} className={`p-3 flex items-start gap-3 ${it._accept ? "bg-background" : "bg-muted/40 opacity-60"}`} data-testid={`material-review-row-${idx}`}>
                  <Checkbox
                    checked={!!it._accept}
                    onCheckedChange={(v) => setExtractedPreview((arr) => arr.map((x, i) => i === idx ? { ...x, _accept: !!v } : x))}
                    data-testid={`material-review-check-${idx}`}
                    className="mt-1"
                  />
                  <div className="flex-1 grid grid-cols-12 gap-2">
                    <Input
                      className="col-span-7 h-8 text-sm"
                      value={it.artikel}
                      onChange={(e) => setExtractedPreview((arr) => arr.map((x, i) => i === idx ? { ...x, artikel: e.target.value } : x))}
                      data-testid={`material-review-artikel-${idx}`}
                    />
                    <Input
                      type="number"
                      step="0.1"
                      className="col-span-2 h-8 text-sm"
                      value={it.menge}
                      onChange={(e) => setExtractedPreview((arr) => arr.map((x, i) => i === idx ? { ...x, menge: e.target.value } : x))}
                      data-testid={`material-review-menge-${idx}`}
                    />
                    <Input
                      className="col-span-3 h-8 text-sm"
                      value={it.einheit || ""}
                      placeholder="Einheit"
                      onChange={(e) => setExtractedPreview((arr) => arr.map((x, i) => i === idx ? { ...x, einheit: e.target.value } : x))}
                      data-testid={`material-review-einheit-${idx}`}
                    />
                    <Input
                      className="col-span-12 h-8 text-xs"
                      value={it.lieferant || ""}
                      placeholder="Lieferant (optional)"
                      onChange={(e) => setExtractedPreview((arr) => arr.map((x, i) => i === idx ? { ...x, lieferant: e.target.value } : x))}
                      data-testid={`material-review-lieferant-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => { setExtractDialogOpen(false); setExtractedPreview([]); }} data-testid="material-review-cancel">
              Abbrechen
            </Button>
            <Button type="button" onClick={commitExtractedMaterials} data-testid="material-review-commit">
              <Plus className="h-4 w-4 mr-2" /> {extractedPreview.filter((x) => x._accept).length} übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
